import type { FormConfig } from "@amzn/aquila-feedback-web-assets";
import type { TemplateAssociations } from "@amzn/d16-g-cdb-publisher-portal-typescript-client";
import {
    useAsync,
    useMountEffect,
    type AsyncStatus,
    type UseAsyncActions,
} from "@react-hookz/web";
import { cloneDeep } from "lodash";
import { useState } from "react";
import { useStore } from "zustand";

import { getPublisherPortalApiClient } from "../../../../api/publisherPortalApi/publisherPortalApi";
import { PublisherWebsiteHost } from "../../../../config/auth.config";
import { authStore } from "../../../../store/auth";
import { reportIssueStore } from "../../../../store/reportIssue";

import { useGetReportIssueForm } from "./useGetReportIssueForm";

interface GetReportIssueFormProps {
    reportIssueFormConfig: { configId: string; awsAccountId: string };
}

const findNodeByFieldDeep = (obj, fieldName) => {
    if (!obj || typeof obj !== "object") return null;

    if (obj.props?.field === fieldName) {
        return obj;
    }

    for (const key in obj) {
        const result = findNodeByFieldDeep(obj[key], fieldName);
        if (result) return result;
    }

    return null;
};

export const useGetAllInsghtsTabReportIssueForm = ({
    reportIssueFormConfig,
}: GetReportIssueFormProps): [
    FormConfig,
    AsyncStatus,
    UseAsyncActions<void, []>,
    (fieldName: string, value: string) => void,
] => {
    const [_, formConfigAsyncStatus, getFormConfigAsyncAction] =
        useGetReportIssueForm({
            reportIssueFormConfig,
        });
    const [formConfigMap, setFormConfig] = useStore(
        reportIssueStore,
        (state) => [state.formConfigMap, state.setFormConfig],
    );
    const [templates, setTemplates] = useState<TemplateAssociations[]>([]);
    const [stage] = useStore(authStore, (state) => [state.settings.stage]);

    const [, onDemandInsightsAction] = useAsync(async () => {
        const { data = [] } = await (
            await getPublisherPortalApiClient()
        ).getTemplatesWithAssociations({});
        return setTemplates(data);
    });

    useMountEffect(() => {
        void onDemandInsightsAction.execute();
    });

    // No longer adding Request Template Access functionality

    const updateFormConfig = (fieldName: string, value: string) => {
        const previsousConfig = cloneDeep(
            reportIssueStore.getState().formConfigMap[
                reportIssueFormConfig.configId
            ] ?? {},
        );
        if (fieldName === "packagedInsightType") {
            const filteredTemplates = templates.filter((template) =>
                template.program?.themes?.includes?.(value),
            );
            const templateItems = filteredTemplates.map((template) => ({
                label: template.name,
                value: `https://${PublisherWebsiteHost[stage]}/domains/${template.domain?.id}/templates/${template.id}`,
            }));
            const node = findNodeByFieldDeep(
                previsousConfig,
                "templateDomainId",
            );
            if (node) {
                node.props.items = templateItems ?? [];
            }
            setFormConfig(reportIssueFormConfig.configId, previsousConfig);
        }

        // TODO: handle aquila footer behavior based on form values
        if (fieldName === "issueType") {
            const submitButton = document.querySelector(
                "button[data-testid='submit-button']",
            );
            if (submitButton) {
                if (
                    value === "Data in PPT slide is missing or looks inaccurate"
                ) {
                    submitButton.setAttribute("disabled", "true");
                } else {
                    submitButton.removeAttribute("disabled");
                }
            }
        }
    };

    return [
        formConfigMap[reportIssueFormConfig.configId],
        formConfigAsyncStatus,
        getFormConfigAsyncAction,
        updateFormConfig,
    ];
};
